import { FunctionComponent, useMemo, useState } from 'react';
import { Drawer, Stack, SvgIcon, Typography, useTheme } from '@mui/material';
import {
    useGetBillingsQuery,
    filterBillingsForApp,
    type Billing,
} from '@starjack/entities/billing';
import { useStarjackConfig } from '@starjack/core';
import { StarjackTable } from '@/components/Table/v2/Table';
import { usePagination } from '@/hooks/usePagination';
import { formatStarjackDate } from '@/utils/time';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import IconButton from '@mui/material/IconButton';
import { useAuthentication } from '@/hooks/useAuthentication';
import { urlBuilder } from '@/api/utils';
import { BillingDrawerContent } from './BillingDrawerContent';
import { fetchAuthWrapper } from '@starjack/api';

export const getBillingExcel = async (
    billingRunId: string,
    liftOperatorId: string
): Promise<string | null> => {
    const response = await fetchAuthWrapper(
        `${urlBuilder(
            'order',
            'billing'
        )}/${billingRunId}/${liftOperatorId}/billing.xlsx`,
        {
            headers: {
                Accept: '*',
                'Content-Type':
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            },
            method: 'GET',
        }
    );

    if (!response.ok) {
        return null;
    }

    return URL.createObjectURL(await response.blob());
};

export const BillingsContent: FunctionComponent = () => {
    const { application } = useStarjackConfig();
    const { liftOperatorData } = useAuthentication();
    const { data, isLoading } = useGetBillingsQuery({
        liftOperatorId: liftOperatorData?.liftOperatorId ?? '',
    });
    const billings = useMemo(
        () =>
            filterBillingsForApp(
                data ?? [],
                application,
                liftOperatorData?.liftOperatorId ?? ''
            ),
        [data, application, liftOperatorData?.liftOperatorId]
    );
    const { currentItems, page, setNextPage, setItemsPerPage, itemsPerPage } =
        usePagination({
            items: billings,
        });
    const theme = useTheme();

    const [drawerConfig, setDrawerConfig] = useState<{
        isOpen: boolean;
        billing: Billing;
    }>({ isOpen: false, billing: null });

    const onCloseDrawer = () => {
        setDrawerConfig((prevState) => ({
            ...prevState,
            isOpen: false,
        }));
    };

    return (
        <>
            <Typography sx={{ mb: 8 }} variant='h4'>
                Abrechnungen
            </Typography>
            <Stack alignItems='flex-start' spacing={theme.page.verticalSpacing}>
                <StarjackTable<Billing>
                    count={billings.length}
                    onRowClick={(rowItem) => {
                        setDrawerConfig({
                            isOpen: true,
                            billing: rowItem,
                        });
                    }}
                    items={currentItems}
                    columns={[
                        {
                            label: 'Erstellt am',
                            itemTransform: (billing) =>
                                formatStarjackDate(billing.createdAt),
                        },
                        {
                            label: 'Abrechnung von',
                            itemTransform: (billing) =>
                                formatStarjackDate(billing.billingPeriodFrom),
                        },
                        {
                            label: 'Abrechnung bis',
                            itemTransform: (billing) =>
                                formatStarjackDate(billing.billingPeriodTo),
                        },
                        {
                            label: 'Freigegeben am',
                            itemTransform: (billing) =>
                                billing.releasedAt
                                    ? formatStarjackDate(billing.releasedAt)
                                    : '-',
                        },
                        {
                            label: 'Abrechnung von',
                            itemTransform: (billing) =>
                                billing.releasedBy
                                    ? String(billing.releasedBy)
                                    : '-',
                        },
                        {
                            label: 'Rechnungen Erfolg',
                            itemTransform: (billing) =>
                                billing.successLiftOperatorIds.length,
                            options: {
                                rowCellProps: {
                                    align: 'right',
                                },
                            },
                        },
                        {
                            label: 'Rechnungen Fehler',
                            itemTransform: (billing) =>
                                billing.errorLiftOperatorIds.length,
                            options: {
                                rowCellProps: {
                                    align: 'right',
                                },
                            },
                        },
                        {
                            label: 'Details',
                            options: {
                                rowCellProps: {
                                    align: 'right',
                                },
                            },
                            itemTransform: (billing) => (
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();

                                        setDrawerConfig({
                                            isOpen: true,
                                            billing,
                                        });
                                    }}
                                >
                                    <SvgIcon>
                                        <ArrowCircleRightOutlinedIcon />
                                    </SvgIcon>
                                </IconButton>
                            ),
                        },
                    ]}
                    currentPage={page}
                    onPageChange={(nextPage) => setNextPage(nextPage)}
                    itemId='id'
                    onRowsPerPageChange={setItemsPerPage}
                    rowsPerPage={itemsPerPage}
                    loading={isLoading}
                />

                <Drawer
                    anchor='right'
                    open={drawerConfig.isOpen}
                    onClose={onCloseDrawer}
                >
                    <BillingDrawerContent
                        billing={drawerConfig.billing}
                        onClose={onCloseDrawer}
                    />
                </Drawer>
            </Stack>
        </>
    );
};
